import {
  Box, Flex, Grid, GridItem, Image, Input, Select, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel,
  Button, Popover, PopoverTrigger, PopoverArrow, PopoverBody, PopoverContent, PopoverCloseButton,
  Menu, MenuButton, MenuList, MenuItem, Checkbox,
} from '@chakra-ui/react'
import MyDiv from './businessProcess.style';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { AlertCard, CustomButton, CustomToast, ActionEditIcon, CustomTable } from '../../../../components';
import { useEffect, useState } from 'react';
import PlusIcon from "../../../../assets/images/Vector.svg";
import { useFetch } from "../../../../hooks/useFetch";
import { IBusinessProcess, IWeekdayGroup, IDepartment, IDepartmentWeekdayFlat } from './model';
import { Column } from "../../../../components/CustomTable/customTable";

const weekdayMap = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const emptyBusinessProcess: IBusinessProcess = {
  id: 0,
  businessProcessName: '',
  status: true,
  weekdayGroups: [],
};

const groupByWeekdays = (flat: IDepartmentWeekdayFlat[]): IWeekdayGroup[] => {
  const map = new Map<string, number[]>();
  flat.forEach(({ departmentId, weekdays }) => {
    let parsed: number[] = [];
    try { parsed = JSON.parse(weekdays || '[]'); } catch { parsed = []; }
    const key = JSON.stringify([...parsed].sort((a, b) => a - b));
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(departmentId);
  });
  return Array.from(map.entries()).map(([weekdays, departmentIds]) => ({ departmentIds, weekdays }));
};

const BusinessProcess = () => {
  const [businessProcesses, setBusinessProcesses] = useState<IBusinessProcess[]>([]);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  const [AlertOpen, setAlertOpen] = useState(false);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [statusChangeSource, setStatusChangeSource] = useState<'table' | 'drawer' | null>(null);
  const [currentBusinessProcess, setCurrentBusinessProcess] = useState<IBusinessProcess>(emptyBusinessProcess);

  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource('table');
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource('drawer');
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedId !== null && pendingStatus !== null) {
      const businessProcessId = statusChangeSource === "table" ? selectedId : currentBusinessProcess.id;
      const response = await fetchApi(`Hrlense_BusinessProcess/toggleBusinessOricessstatus/${businessProcessId}`, "PUT", { Id: businessProcessId, Status: pendingStatus });
      if (response) {
        if (statusChangeSource === "table") {
          setBusinessProcesses((prev) => prev.map((bp) => bp.id === selectedId ? { ...bp, status: pendingStatus } : bp));
        } else {
          setCurrentBusinessProcess((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
  };

  const handleAddNewBusinessProcess = () => {
    setIsEditMode(false);
    setSelectedId(null);
    setCurrentBusinessProcess(emptyBusinessProcess);
    setDrawerOpen(true);
  };

  // const handleEditBusinessProcess = (bp: any) => {
  //   setIsEditMode(true);
  //   setSelectedId(bp.id);
  //   setCurrentBusinessProcess({
  //     id: bp.id,
  //     businessProcessName: bp.businessProcessName,
  //     status: bp.status,
  //     weekdayGroups: groupByWeekdays(bp.departmentWeekdays || []),
  //   });
  //   setDrawerOpen(true);
  // };

  const handleEditBusinessProcess = (bp: IBusinessProcess) => {
    setIsEditMode(true);
    setSelectedId(bp.id);
    setCurrentBusinessProcess({
      id: bp.id,
      businessProcessName: bp.businessProcessName,
      status: bp.status,
      weekdayGroups: bp.weekdayGroups.map(g => ({
        departmentIds: [...g.departmentIds],
        weekdays: g.weekdays,
      })),
    });
    setDrawerOpen(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    if (!/^[A-Za-z\s]*$/.test(value)) {
      return;
    }
    setCurrentBusinessProcess(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ---- weekend group helpers ----

  const addWeekdayGroup = () => {
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: [...prev.weekdayGroups, { departmentIds: [], weekdays: '[]' }]
    }));
  };

  const removeWeekdayGroup = (groupIndex: number) => {
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: prev.weekdayGroups.filter((_, i) => i !== groupIndex)
    }));
  };

  const toggleDepartmentInGroup = (groupIndex: number, deptId: number) => {
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: prev.weekdayGroups.map((g, i) => {
        if (i !== groupIndex) return g;
        const exists = g.departmentIds.includes(deptId);
        return {
          ...g,
          departmentIds: exists ? g.departmentIds.filter(id => id !== deptId) : [...g.departmentIds, deptId]
        };
      })
    }));
  };

  const selectAllRemainingInGroup = (groupIndex: number) => {
    const assignedElsewhere = new Set(
      currentBusinessProcess.weekdayGroups.flatMap((g, i) => i === groupIndex ? [] : g.departmentIds)
    );
    const available = departments.filter(d => !assignedElsewhere.has(d.id)).map(d => d.id);
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: prev.weekdayGroups.map((g, i) => i === groupIndex ? { ...g, departmentIds: available } : g)
    }));
  };

  const clearDepartmentsInGroup = (groupIndex: number) => {
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: prev.weekdayGroups.map((g, i) => i === groupIndex ? { ...g, departmentIds: [] } : g)
    }));
  };

  const toggleWeekdayInGroup = (groupIndex: number, dayIndex: number) => {
    setCurrentBusinessProcess(prev => ({
      ...prev,
      weekdayGroups: prev.weekdayGroups.map((g, i) => {
        if (i !== groupIndex) return g;
        const selected: number[] = JSON.parse(g.weekdays || '[]');
        const updated = selected.includes(dayIndex) ? selected.filter(d => d !== dayIndex) : [...selected, dayIndex];
        return { ...g, weekdays: JSON.stringify(updated) };
      })
    }));
  };

  const handleSubmit = async () => {
    if (!currentBusinessProcess.businessProcessName.trim()) {
      addToast({ message: "Please enter Business Process Name", status: "warning" });
      return;
    }
    if (currentBusinessProcess.weekdayGroups.length === 0) {
      addToast({ message: "Please add at least one weekend group", status: "warning" });
      return;
    }
    const invalidGroup = currentBusinessProcess.weekdayGroups.some(
      g => g.departmentIds.length === 0 || JSON.parse(g.weekdays || '[]').length === 0
    );
    if (invalidGroup) {
      addToast({ message: "Every group needs at least one department and one weekoff", status: "warning" });
      return;
    }

    const departmentWeekdays: IDepartmentWeekdayFlat[] = currentBusinessProcess.weekdayGroups.flatMap(g =>
      g.departmentIds.map(departmentId => ({ departmentId, weekdays: g.weekdays }))
    );

    const payload = {
      businessProcessName: currentBusinessProcess.businessProcessName,
      status: currentBusinessProcess.status,
      departmentWeekdays,
    };

    if (isEditMode) {
      const response = await fetchApi(`Hrlense_BusinessProcess/${currentBusinessProcess.id}`, "PUT",
        { id: currentBusinessProcess.id, ...payload }, null, "Business Process updated successfully");
      if (response) {
        setBusinessProcesses((prev) => prev.map((bp) => (bp.id === selectedId ? { ...bp, ...payload, id: currentBusinessProcess.id, weekdayGroups: currentBusinessProcess.weekdayGroups } as any : bp)));
        loadScores();
      }
    } else {
      const response = await fetchApi<any>("Hrlense_BusinessProcess", "POST", payload, null, "business process added successfully");
      if (response) {
        setBusinessProcesses((prev) => [...prev, { ...response, weekdayGroups: groupByWeekdays(response.departmentWeekdays || []) }]);
      }
    }
    setDrawerOpen(false);
  };

  const loadScores = async () => {
    const response = await fetchApi<any[]>("Hrlense_BusinessProcess", "GET");
    if (response) {
      setBusinessProcesses(response.map(bp => ({
        id: bp.id,
        businessProcessName: bp.businessProcessName,
        status: bp.status,
        weekdayGroups: groupByWeekdays(bp.departmentWeekdays || []),
      })));
    }
  };

  const loadDepartments = async () => {
    const response = await fetchApi<IDepartment[]>("DropDown/department", "GET");
    if (response) setDepartments(response);
  };

  useEffect(() => {
    loadScores();
    loadDepartments();
  }, [])

  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");
  const getDepartmentName = (deptId: number) => departments.find(d => d.id === deptId)?.departmentName ?? `Dept #${deptId}`;

  return (
    <MyDiv>
      <Flex className="page_heading flex_header" >
        <Text className="font-poppins text_semibold text_3xl">Business Process</Text>
        <Flex gap={2}>
          <CustomButton title="Add New Business Process" className=' btn font-poppins text_sm text_medium' rightIcon={<Image src={PlusIcon} className='logo' />} onClick={handleAddNewBusinessProcess} />
        </Flex>
      </Flex>
      <Grid className='grid_container'>
        <GridItem colSpan={12}>
          <CustomTable
            value={businessProcesses}
            emptyMessage="No Business Processes Found"
          >
            <Column header="S. No" body={(_, index) => index + 1} />
            <Column header="Business Process Name" field='businessProcessName' />
            <Column
              header="Departments & Week Off"
              body={(bp: IBusinessProcess) => {
                const totalDepts = bp.weekdayGroups.reduce((sum, g) => sum + g.departmentIds.length, 0);
                return (
                  <Popover>
                    <PopoverTrigger>
                      <Text cursor="pointer" className="weekDays font-poppins text_md text_regular">
                        {totalDepts} Department{totalDepts !== 1 ? 's' : ''}
                      </Text>
                    </PopoverTrigger>
                    <PopoverContent width="360px">
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverBody>
                        <TableContainer>
                          <Table variant="outline" size="sm">
                            <Thead>
                              <Tr className="weekDays font-poppins text_md text_regular">
                                <Th>S.No</Th>
                                <Th>Departments</Th>
                                <Th>Week Off</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {bp.weekdayGroups.length === 0 ? (
                                <Tr>
                                  <Td colSpan={3}>No departments assigned</Td>
                                </Tr>
                              ) : (
                                bp.weekdayGroups.map((g, index) => {
                                  let days: string[] = [];
                                  try {
                                    const parsed = JSON.parse(g.weekdays || "[]");
                                    days = parsed.map((d: string | number) => weekdayMap[parseInt(String(d))]);
                                  } catch (err) {
                                    console.error("Failed to parse weekdays:", err);
                                  }
                                  return (
                                    <Tr key={index} className="weekDays font-poppins text_md text_regular">
                                      <Td>{index + 1}</Td>
                                      <Td>{g.departmentIds.map(getDepartmentName).join(', ')}</Td>
                                      <Td>{days.length ? days.join(', ') : 'Invalid Weekdays'}</Td>
                                    </Tr>
                                  );
                                })
                              )}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                );
              }}
            />
            <Column
              header="Status"
              body={(bp: IBusinessProcess) => (
                <Select
                  value={bp.status ? "true" : "false"}
                  onChange={(e) => handleStatusChange(bp.id, e.target.value === "true")}
                  width="78px"
                  height="24px"
                  icon={<ChevronDownIcon />}
                  borderRadius="4px"
                  color={getStatusColor(bp.status)}
                  className="font-poppins text_medium text_xsm"
                >
                  <option value="true" style={{ color: "#38A169" }}>Active</option>
                  <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                </Select>
              )}
            />
            <Column
              header="Actions"
              body={(bp: IBusinessProcess) => (
                <ActionEditIcon onClick={() => handleEditBusinessProcess(bp)} />
              )}
            />
          </CustomTable>

        </GridItem>
      </Grid>
      <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />

      <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">{isEditMode ? 'Edit Business Process' : 'Add New Business Process'}</DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Box py={4} overflowY="auto">
              <FormControl mb={4} isRequired>
                <FormLabel>Business Process Name</FormLabel>
                <Input name="businessProcessName" value={currentBusinessProcess.businessProcessName} onChange={handleInputChange} placeholder="Enter Business Process Name" />
              </FormControl>

              <FormControl mb={2}>
                <Flex justify="space-between" align="center">
                  <FormLabel mb={0}>Weekend Groups</FormLabel>
                  <Button size="xs" onClick={addWeekdayGroup}>+ Add Group</Button>
                </Flex>
              </FormControl>

              {currentBusinessProcess.weekdayGroups.length === 0 && (
                <Text fontSize="sm" color="gray.500" mb={4}>
                  No groups yet. Click "+ Add Group" to assign departments and their week off.
                </Text>
              )}

              {currentBusinessProcess.weekdayGroups.map((group, groupIndex) => {
                const assignedElsewhere = new Set(
                  currentBusinessProcess.weekdayGroups.flatMap((g, i) => i === groupIndex ? [] : g.departmentIds)
                );
                const availableDepts = departments.filter(d => !assignedElsewhere.has(d.id));
                const selectedDays: number[] = (() => {
                  try { return JSON.parse(group.weekdays || '[]'); } catch { return []; }
                })();

                return (
                  <Box key={groupIndex} border="1px solid #E2E8F0" borderRadius="6px" p={3} mb={4}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontWeight="semibold" fontSize="sm">Group {groupIndex + 1}</Text>
                      <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeWeekdayGroup(groupIndex)}>Remove</Button>
                    </Flex>

                    <FormControl mb={3} isRequired>
                      <Flex justify="space-between" align="center">
                        <FormLabel fontSize="sm">Departments</FormLabel>
                        <Flex gap={2}>
                          <Button size="xs" variant="link" onClick={() => selectAllRemainingInGroup(groupIndex)}>Select all remaining</Button>
                          <Button size="xs" variant="link" colorScheme="red" onClick={() => clearDepartmentsInGroup(groupIndex)}>Clear</Button>
                        </Flex>
                      </Flex>
                      <Menu closeOnSelect={false}>
                        <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" w="100%" textAlign="left" fontWeight="normal" size="sm">
                          {group.departmentIds.length ? `${group.departmentIds.length} selected` : "Select Departments"}
                        </MenuButton>
                        <MenuList maxH="240px" overflowY="auto" zIndex={1500}>
                          {availableDepts.length === 0 ? (
                            <MenuItem isDisabled>No departments available</MenuItem>
                          ) : (
                            availableDepts.map(dept => (
                              <MenuItem key={dept.id} onClick={() => toggleDepartmentInGroup(groupIndex, dept.id)}>
                                <Checkbox isChecked={group.departmentIds.includes(dept.id)} pointerEvents="none" mr={2} />
                                {dept.departmentName}
                              </MenuItem>
                            ))
                          )}
                        </MenuList>
                      </Menu>
                      {group.departmentIds.length > 0 && (
                        <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>
                          {group.departmentIds.map(getDepartmentName).join(', ')}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Week Off</FormLabel>
                      <Flex flexWrap="wrap" gap={2}>
                        {weekdayMap.map((day, index) => {
                          const isSelected = selectedDays.includes(index);
                          return (
                            <Button
                              key={index}
                              size="sm"
                              variant={isSelected ? 'solid' : 'outline'}
                              colorScheme={isSelected ? 'green' : 'gray'}
                              onClick={() => toggleWeekdayInGroup(groupIndex, index)}
                            >
                              {day}
                            </Button>
                          );
                        })}
                      </Flex>
                    </FormControl>
                  </Box>
                );
              })}

              <FormControl mb={4}>
                <FormLabel>Status</FormLabel>
                <Select w="120px" value={currentBusinessProcess.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentBusinessProcess.status)}>
                  <option value="true" style={{ color: "#38A169" }}> Active</option>
                  <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                </Select>
              </FormControl>
            </Box>
            <Flex justify="flex-end" mt={4} gap={2}>
              <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: '#D92F34', color: 'white' }}>Cancel</Button>
              <Button onClick={handleSubmit} className="font-poppins text_sm text_medium" style={{ backgroundColor: '#006838', color: 'white' }}>{isEditMode ? "Update Business Process" : "Add Business Process"}</Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </MyDiv>
  )
}
export default BusinessProcess;